/**
 * KanbanBoard Component
 * 
 * Componente principale per la visualizzazione del Kanban board
 * Supporta drag & drop per spostare task tra colonne
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Group,
  Paper,
  Stack,
  Title,
  Badge,
  Loader,
  Text,
  Button,
  ActionIcon,
  Menu,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconDotsVertical,
  IconRefresh,
  IconSettings,
  IconFilter,
} from '@tabler/icons-react';

import { KanbanColumn } from '../../types/api';
import { kanbanService, KanbanBoardData } from '../../services/kanbanService';
import { checklistItemService } from '../../services/checklistItemService';
import KanbanColumnComponent from './KanbanColumn';
import KanbanCard from './KanbanCard';
import TaskDetailModal from '../modals/TaskDetailModal';

interface KanbanBoardProps {
  requestId?: string;
  height?: string | number;
  onTaskCreate?: () => void;
  onTaskUpdate?: () => void;
  onTaskClick?: (taskId: number) => void;
  onRefreshBoard?: (refreshFn: () => Promise<void>) => void;
  showHeader?: boolean;
  showFilters?: boolean;
  allowCreateTask?: boolean;
}

interface DraggedTask {
  id: number;
  title: string;
  column: KanbanColumn;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  requestId,
  height = '70vh',
  onTaskCreate,
  onTaskUpdate,
  onTaskClick,
  onRefreshBoard,
  showHeader = true,
  showFilters = true,
  allowCreateTask = true,
}) => {
  const [boardData, setBoardData] = useState<KanbanBoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState<DraggedTask | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Le notifiche vengono gestite tramite l'import diretto di notifications

  // Configurazione sensori per drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Richiede un movimento di 8px prima di iniziare il drag
      },
    })
  );

  // Caricamento dati iniziale
  const loadBoardData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await kanbanService.getKanbanBoard(requestId);
      setBoardData(data);
    } catch (error) {
      console.error('Errore nel caricamento del Kanban board:', error);
      notifications.show({
        title: 'Errore',
        message: 'Impossibile caricare il Kanban board',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [requestId, notifications]);

  // Refresh del board
  const refreshBoard = useCallback(async () => {
    try {
      setRefreshing(true);
      await loadBoardData();
      notifications.show({
        title: 'Aggiornato',
        message: 'Kanban board aggiornato con successo',
        color: 'green',
      });
    } catch (error) {
      // L'errore è già gestito in loadBoardData
    } finally {
      setRefreshing(false);
    }
  }, [loadBoardData, notifications]);

  // Caricamento iniziale
  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  // Espone la funzione refresh al componente padre
  useEffect(() => {
    if (onRefreshBoard) {
      onRefreshBoard(refreshBoard);
    }
  }, [onRefreshBoard, refreshBoard]);

  // Gestione inizio drag
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const taskId = Number(active.id);
    
    // Trova il task in tutte le colonne
    let foundTask: DraggedTask | null = null;
    if (boardData) {
      Object.entries(boardData.columns).forEach(([columnId, column]) => {
        const task = column.tasks.find(t => t.id === taskId);
        if (task) {
          foundTask = {
            id: task.id,
            title: task.title || task.name, // Usa title come campo principale
            column: Number(columnId) as KanbanColumn,
          };
        }
      });
    }
    
    setDraggedTask(foundTask);
  }, [boardData]);

  // Gestione fine drag
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedTask(null);

    if (!over || !boardData) return;

    const taskId = Number(active.id);
    const overId = String(over.id);
    
    // Determina se stiamo spostando su una colonna o su un altro task
    const isColumn = Object.values(KanbanColumn).includes(Number(overId));
    
    let targetColumn: KanbanColumn | null = null;
    let newPosition: number = 0;

    if (isColumn) {
      // Spostamento diretto su una colonna
      targetColumn = Number(overId) as KanbanColumn;
      newPosition = boardData.columns[targetColumn].tasks.length;
    } else {
      // Spostamento su un altro task - trova la colonna e la posizione
      let found = false;
      Object.entries(boardData.columns).forEach(([columnId, column]) => {
        if (!found) {
          const targetTaskIndex = column.tasks.findIndex(t => t.id === Number(overId));
          if (targetTaskIndex !== -1) {
            targetColumn = Number(columnId) as KanbanColumn;
            newPosition = targetTaskIndex;
            found = true;
          }
        }
      });
      
      if (!found || targetColumn === null) return;
    }

    try {
      // Trova il task corrente e la sua colonna
      let currentColumn: KanbanColumn | null = null;
      let currentTask: any = null;
      
      Object.entries(boardData.columns).forEach(([columnId, column]) => {
        const task = column.tasks.find(t => t.id === taskId);
        if (task) {
          currentColumn = Number(columnId) as KanbanColumn;
          currentTask = task;
        }
      });

      if (!currentColumn || !currentTask || targetColumn === null) return;

      // Se stiamo spostando nella stessa colonna, aggiorna solo la posizione
      if (currentColumn === targetColumn) {
        await kanbanService.updateTaskPosition(taskId, newPosition);
      } else {
        // Spostamento tra colonne diverse
        await kanbanService.moveTaskBetweenColumns(
          taskId,
          currentColumn,
          targetColumn,
          newPosition
        );
      }

      // Ricarica i dati
      await loadBoardData();
      
      notifications.show({
        title: 'Task spostato',
        message: `Task "${currentTask?.title || currentTask?.name || 'Unknown'}" spostato con successo`,
        color: 'green',
      });

      // Chiama callback se fornito
      onTaskUpdate?.();

    } catch (error) {
      console.error('Errore nello spostamento del task:', error);
      notifications.show({
        title: 'Errore',
        message: 'Impossibile spostare il task',
        color: 'red',
      });
    }
  }, [boardData, loadBoardData, notifications, onTaskUpdate]);

  // Gestione apertura dettaglio task
  const handleTaskClick = useCallback((taskId: number) => {
    // Chiama solo la callback esterna, non gestisce il modal internamente
    if (onTaskClick) {
      onTaskClick(taskId);
    }
  }, [onTaskClick]);

  // Gestione aggiornamento task dalla modal
  const handleTaskUpdated = useCallback(async () => {
    await loadBoardData();
    onTaskUpdate?.();
  }, [loadBoardData, onTaskUpdate]);

  if (loading) {
    return (
      <Paper p="xl" style={{ height }}>
        <Group justify="center" align="center" style={{ height: '100%' }}>
          <Stack align="center">
            <Loader size="lg" />
            <Text>Caricamento Kanban board...</Text>
          </Stack>
        </Group>
      </Paper>
    );
  }

  if (!boardData) {
    return (
      <Paper p="xl" style={{ height }}>
        <Group justify="center" align="center" style={{ height: '100%' }}>
          <Stack align="center">
            <Text c="dimmed">Nessun dato disponibile</Text>
            <Button onClick={loadBoardData}>Riprova</Button>
          </Stack>
        </Group>
      </Paper>
    );
  }

  const columns = Object.values(boardData.columns);

  return (
    <Box style={{ height }}>
      {/* Header del board */}
      {showHeader && (
        <Paper p="md" mb="md" withBorder>
          <Group justify="space-between">
            <Group>
              <Title order={3}>Kanban Board</Title>
              <Badge variant="light" color="blue">
                {boardData.totalTasks} task
              </Badge>
            </Group>
            
            <Group>
              {showFilters && (
                <Tooltip label="Filtri">
                  <ActionIcon variant="subtle">
                    <IconFilter size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
              
              <Tooltip label="Aggiorna">
                <ActionIcon 
                  variant="subtle" 
                  onClick={refreshBoard}
                  loading={refreshing}
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>

              {allowCreateTask && (
                <Button 
                  leftSection={<IconPlus size={16} />}
                  onClick={onTaskCreate}
                  size="sm"
                >
                  Nuovo Task
                </Button>
              )}

              <Menu>
                <Menu.Target>
                  <ActionIcon variant="subtle">
                    <IconDotsVertical size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconSettings size={16} />}>
                    Impostazioni board
                  </Menu.Item>
                  <Menu.Item leftSection={<IconRefresh size={16} />} onClick={refreshBoard}>
                    Aggiorna
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Paper>
      )}

      {/* Kanban Board con DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Paper 
          p="md" 
          style={{ 
            height: showHeader ? 'calc(100% - 80px)' : '100%',
            overflow: 'hidden'
          }}
          withBorder
        >
          <Group 
            align="start" 
            gap="md" 
            style={{ height: '100%' }}
            wrap="nowrap"
          >
            {columns.map((column) => (
              <SortableContext
                key={column.id}
                id={String(column.id)}
                items={column.tasks.map(task => String(task.id))}
                strategy={verticalListSortingStrategy}
              >
                <KanbanColumnComponent
                  column={column}
                  onTaskClick={handleTaskClick}
                />
              </SortableContext>
            ))}
          </Group>
        </Paper>

        {/* Overlay per il drag */}
        <DragOverlay>
          {draggedTask && (
            <KanbanCard
              task={{
                id: draggedTask.id,
                title: draggedTask.title,
                // Aggiungi altre proprietà necessarie con valori di default
                description: '',
                status: 'todo',
                priority: 'Medium', // Ora usa stringa invece di numero
                completionPercentage: 0,
                checklistItemsCount: 0,
                completedChecklistItemsCount: 0,
                isOverdue: false,
              }}
              isDragging
              onClick={() => {}}
            />
          )}
        </DragOverlay>
      </DndContext>

      {/* Modal dettaglio task - Rimosso da qui, gestito dal componente padre */}
    </Box>
  );
};

export default KanbanBoard;